import { Assessment, ExpandLess, ExpandMore } from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import LayersIcon from '@mui/icons-material/Layers';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import TopicIcon from '@mui/icons-material/Topic';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { Collapse, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, styled, Theme, useTheme } from '@mui/material/styles';
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { Feature, PermissionContext } from '../../contexts/PermissionContext';
import { SearchContext } from "../../contexts/SearchContext";




const drawerWidth = 240;

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

interface SideBarProps {
    open?: boolean;
    pinned?: boolean;
    handleClose: (unpin: boolean) => void;
    handleOpen: (pin: boolean) => void;
}

export default function Sidebar(props: SideBarProps) {


    const [storageOpen, setStorageOpen] = useState(false);
    const [contractOpen, setContractOpen] = useState(false);
    const [dispatchOpen, setDispatchOpen] = useState(false);
    const [auditOpen, setAuditOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    const searchContext = useContext(SearchContext);
    const permissionContext = useContext(PermissionContext);

    const theme = useTheme();

    const focusSearchInput = () => {
        document.getElementById('main-search-bar')?.focus();
    }

    return (
        <Drawer variant="permanent" open={props.open}
            onMouseEnter={props.pinned ? undefined : () => props.handleOpen(false)}
            onMouseLeave={props.pinned ? undefined : () => props.handleClose(false)}
        >
            <DrawerHeader>
                <IconButton onClick={() => props.handleClose(true)}>
                    {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>

            </DrawerHeader>
            <Divider />
            <List>
                {/*  Search - should just jump to the search page; same as starting to type in the search bar  */}
                <Tooltip title="Search Inventory" placement="right">
                    <ListItemButton
                        key="Search"
                        sx={{
                            minHeight: 48,
                            justifyContent: props.open ? 'initial' : 'center',
                            px: 2.5,
                        }}
                        onClick={focusSearchInput}
                        component={Link}
                        to={"/"}

                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: props.open ? 3 : 'auto',
                                justifyContent: 'center',
                            }}
                        >
                            <SearchIcon />
                        </ListItemIcon>
                        <ListItemText primary="Search" sx={{ opacity: props.open ? 1 : 0 }} />
                    </ListItemButton>
                </Tooltip>

                {permissionContext.isPermitted(Feature.StorageRequest) &&
                    <span onMouseLeave={() => { setStorageOpen(false) }}>
                        <Tooltip title="Storage Request" placement="right">
                            <ListItemButton
                                key="Storage"
                                sx={{
                                    minHeight: 48,
                                    justifyContent: props.open ? 'initial' : 'center',
                                    px: 2.5,
                                }}

                                component={Link}
                                to={"/storagerequest"}

                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: props.open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <WarehouseIcon />
                                </ListItemIcon>
                                <ListItemText primary="Storage" sx={{ opacity: props.open ? 1 : 0 }} />

                                {props.open &&
                                    <IconButton onMouseEnter={() => { setStorageOpen(true) }}>
                                        {props.open && (storageOpen ?
                                            <ExpandLess /> :
                                            <ExpandMore />)}
                                    </IconButton>
                                }


                            </ListItemButton>
                        </Tooltip>
                        {props.open &&
                            <Collapse in={storageOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <ListItemButton sx={{ pl: 4 }} component={Link} to="/storagerequest/create">
                                        <ListItemIcon>
                                            <AddIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="New Request" />
                                    </ListItemButton>
                                    <ListItemButton sx={{ pl: 4 }} component={Link} to="/storagerequest">
                                        <ListItemIcon>
                                            <LayersIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="All Requests" />
                                    </ListItemButton>
                                </List>
                            </Collapse>
                        }
                    </span>
                }

                {permissionContext.isPermitted(Feature.Contract) &&
                    <span onMouseLeave={() => { setContractOpen(false) }}>
                        <Tooltip title="Contract / Lease Spare List" placement="right">
                            <ListItemButton
                                key="Contract"
                                sx={{
                                    minHeight: 48,
                                    justifyContent: props.open ? 'initial' : 'center',
                                    px: 2.5,
                                }}
                                component={Link}
                                to={"/contract"}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: props.open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <TopicIcon />
                                </ListItemIcon>
                                <ListItemText primary="Contract / Lease" sx={{ opacity: props.open ? 1 : 0 }} />
                                {props.open &&
                                    <IconButton onMouseEnter={() => { setContractOpen(true); }}>
                                        {props.open && (contractOpen ?
                                            <ExpandLess /> :
                                            <ExpandMore />)}
                                    </IconButton>
                                }
                            </ListItemButton>
                        </Tooltip>
                        {props.open &&
                            <Collapse in={contractOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <ListItemButton sx={{ pl: 4 }} component={Link} to="/contract/create">
                                        <ListItemIcon>
                                            <AddIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="New Request" />
                                    </ListItemButton>

                                    <ListItemButton sx={{ pl: 4 }} component={Link} to="/contract">
                                        <ListItemIcon>
                                            <LayersIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="All" />
                                    </ListItemButton>
                                </List>
                            </Collapse>
                        }
                    </span>
                }


                {/* Enquiry - DESCOPED */}
                {/* 
                {permissionContext.isPermitted(Feature.Enquiry) &&
                    <span onMouseLeave={() => { setEnquiryOpen(false) }}>
                        <Tooltip title="General Enquiry" placement="right">
                            <ListItemButton
                                key="Enquiry"
                                sx={{
                                    minHeight: 48,
                                    justifyContent: props.open ? 'initial' : 'center',
                                    px: 2.5,
                                }}
                                onClick={() => { setEnquiryOpen(!enquiryOpen) }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: props.open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ContactSupportIcon />
                                </ListItemIcon>
                                <ListItemText primary="Enquiry" sx={{ opacity: props.open ? 1 : 0 }} />
                                {props.open &&
                                    <IconButton onMouseEnter={() => { setEnquiryOpen(true) }}>
                                        {props.open && (enquiryOpen ?
                                            <ExpandLess /> :
                                            <ExpandMore />)}
                                    </IconButton>
                                }
                            </ListItemButton>
                        </Tooltip>
                        {props.open &&
                            <Collapse in={enquiryOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <ListItemButton sx={{ pl: 4 }}>
                                        <ListItemIcon>
                                            <AddIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="New Enquiry" />
                                    </ListItemButton>
                                    <ListItemButton sx={{ pl: 4 }}>
                                        <ListItemIcon>
                                            <LayersIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="All Enquiries" />
                                    </ListItemButton>
                                </List>
                            </Collapse>
                        }
                    </span>
                }
                */}

                {/*  Dispatch  */}
                {permissionContext.isPermitted(Feature.DispatchRequest) &&
                    <span onMouseLeave={() => { setDispatchOpen(false) }}>
                        <Tooltip title="Dispatch Request" placement="right">
                            <ListItemButton
                                key="Dispatch"
                                sx={{
                                    minHeight: 48,
                                    justifyContent: props.open ? 'initial' : 'center',
                                    px: 2.5,
                                }}
                                component={Link}
                                to={"/dispatchrequest"}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: props.open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <LocalShippingIcon />
                                </ListItemIcon>
                                <ListItemText primary="Dispatch" sx={{ opacity: props.open ? 1 : 0 }} />
                                {props.open &&
                                    <IconButton onMouseEnter={() => { setDispatchOpen(true) }}>
                                        {props.open && (dispatchOpen ?
                                            <ExpandLess /> :
                                            <ExpandMore />)}
                                    </IconButton>
                                }
                            </ListItemButton>
                        </Tooltip>

                        {props.open &&
                            <Collapse in={dispatchOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <ListItemButton sx={{ pl: 4 }} component={Link} to="/dispatchrequest/create">
                                        <ListItemIcon>
                                            <AddIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="New Request" />
                                    </ListItemButton>
                                    <ListItemButton sx={{ pl: 4 }} component={Link} to="/dispatchrequest">
                                        <ListItemIcon>
                                            <LayersIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="All Requests" />
                                    </ListItemButton>
                                </List>
                            </Collapse>
                        }
                    </span>
                }
                {/*  Disposal - DESCOPED  */}
                {/*
                <span onMouseLeave={() => { setDisposalOpen(false) }}>
                    <Tooltip title="Disposal Request" placement="right">
                        <ListItemButton
                            key="Disposal"
                            sx={{
                                minHeight: 48,
                                justifyContent: props.open ? 'initial' : 'center',
                                px: 2.5,
                            }}
                            onClick={() => { setDisposalOpen(!disposalOpen) }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: props.open ? 3 : 'auto',
                                    justifyContent: 'center',
                                }}
                            >
                                <DeleteSweepIcon />
                            </ListItemIcon>
                            <ListItemText primary="Disposal" sx={{ opacity: props.open ? 1 : 0 }} />
                            {props.open &&
                                <IconButton onMouseEnter={() => { setDisposalOpen(true) }}>
                                    {props.open && (disposalOpen ?
                                        <ExpandLess /> :
                                        <ExpandMore />)}
                                </IconButton>
                            }
                        </ListItemButton>
                    </Tooltip>

                    {props.open &&
                        <Collapse in={disposalOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <ListItemButton sx={{ pl: 4 }}>
                                    <ListItemIcon>
                                        <AddIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="New Request" />
                                </ListItemButton>
                                <ListItemButton sx={{ pl: 4 }}>
                                    <ListItemIcon>
                                        <LayersIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="All Requests" />
                                </ListItemButton>
                            </List>
                        </Collapse>
                    }
                </span>
                */ }


                {/*  Audit  */}
                {permissionContext.isPermitted(Feature.Audit) &&
                    <span onMouseLeave={() => { setAuditOpen(false) }}>
                        <Tooltip title="Material Audit" placement="right">
                            <ListItemButton
                                key="Audit"
                                sx={{
                                    minHeight: 48,
                                    justifyContent: props.open ? 'initial' : 'center',
                                    px: 2.5,
                                }}
                                component={Link}
                                to={"/audit"}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: props.open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <VerifiedUserIcon />
                                </ListItemIcon>
                                <ListItemText primary="Audit" sx={{ opacity: props.open ? 1 : 0 }} />
                                {props.open &&
                                    <IconButton onMouseEnter={() => { setAuditOpen(true) }}>
                                        {props.open && (auditOpen ?
                                            <ExpandLess /> :
                                            <ExpandMore />)}
                                    </IconButton>
                                }

                            </ListItemButton>
                        </Tooltip>
                        {props.open &&
                            <Collapse in={auditOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <ListItemButton sx={{ pl: 4 }} onClick={() => searchContext.auditResults()}>
                                        <ListItemIcon>
                                            <AddIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="New Audit" />
                                    </ListItemButton>
                                    <ListItemButton sx={{ pl: 4 }} component={Link} to="/audit">
                                        <ListItemIcon>
                                            <LayersIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="All Audits" />
                                    </ListItemButton>
                                </List>
                            </Collapse>
                        }
                    </span>
                }


                {/*  Reports  */}
                {permissionContext.isPermitted(Feature.Reports) &&
                    <span onMouseLeave={() => { setReportOpen(false) }}>
                        <Tooltip title="Reporting" placement="right">
                            <ListItemButton
                                key="Report"
                                sx={{
                                    minHeight: 48,
                                    justifyContent: props.open ? 'initial' : 'center',
                                    px: 2.5,
                                }}
                                component={Link}
                                to={"/report"}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: props.open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Assessment />
                                </ListItemIcon>
                                <ListItemText primary="Report" sx={{ opacity: props.open ? 1 : 0 }} />
                                {props.open &&
                                    <IconButton onMouseEnter={() => { setReportOpen(true) }}>
                                        {props.open && (reportOpen ?
                                            <ExpandLess /> :
                                            <ExpandMore />)}
                                    </IconButton>
                                }

                            </ListItemButton>
                        </Tooltip>
                        {props.open &&
                            <Collapse in={reportOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <ListItemButton sx={{ pl: 4 }} component={Link} to="/report/storage">
                                        <ListItemIcon>
                                            <WarehouseIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Storage" />
                                    </ListItemButton>
                                    <ListItemButton sx={{ pl: 4 }} component={Link} to="/report/dispatch">
                                        <ListItemIcon>
                                            <LocalShippingIcon />
                                        </ListItemIcon>
                                        <ListItemText primary="Dispatch" />
                                    </ListItemButton>
                                </List>
                            </Collapse>
                        }
                    </span>
                }


            </List>
            <Divider />
            <List>
                <ListItemButton
                    key="Sign out"
                    sx={{
                        minHeight: 48,
                        justifyContent: props.open ? 'initial' : 'center',
                        px: 2.5,
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: props.open ? 3 : 'auto',
                            justifyContent: 'center',
                        }}
                    >
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary="Sign out" sx={{ opacity: props.open ? 1 : 0 }} />
                </ListItemButton>
            </List>
        </Drawer>
    );
}
